import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

/**
 * Custom hook to use the auth context
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider Component
 * Manages authentication state and provides auth methods to child components
 */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Create a new user account with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {Object} profileData - Additional user profile data
   * @returns {Promise<Object>} User credential
   */
  const signup = async (email, password, profileData) => {
    try {
      setError(null);
      console.log('[Signup] Starting signup process for:', email);

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('[Signup] User account created successfully:', user.uid);

      // Update display name
      if (profileData.displayName) {
        await updateProfile(user, {
          displayName: profileData.displayName
        });
        console.log('[Signup] Display name updated:', profileData.displayName);
      }

      // Create user profile in Firestore
      console.log('[Signup] Creating Firestore document for user:', user.uid);
      const userDocRef = doc(db, 'users', user.uid);
      const firestoreData = {
        uid: user.uid,
        email: user.email,
        displayName: profileData.displayName || '',
        role: profileData.role || 'client', // 'trainer' or 'client'
        phoneNumber: profileData.phoneNumber || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        emailVerified: user.emailVerified,
        // Role-specific data
        ...(profileData.role === 'trainer' ? {
          businessName: profileData.businessName || '',
          certifications: profileData.certifications || [],
          specializations: profileData.specializations || [],
          clientCount: 0
        } : {
          trainerId: profileData.trainerId || null,
          subscription: 'free',
          goals: profileData.goals || []
        })
      };
      console.log('[Signup] Firestore data to be written:', firestoreData);

      await setDoc(userDocRef, firestoreData);
      console.log('[Signup] Firestore document created successfully');

      // Send email verification
      await sendEmailVerification(user);
      console.log('[Signup] Verification email sent');

      return userCredential;
    } catch (error) {
      console.error('[Signup] Error during signup:', error);
      console.error('[Signup] Error code:', error.code);
      console.error('[Signup] Error message:', error.message);
      setError(error.message);
      throw error;
    }
  };

  /**
   * Sign in with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} User credential
   */
  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Detect if user is on mobile device
   * @returns {boolean} True if on mobile
   */
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  /**
   * Sign in with Google (for login, not signup)
   * Uses redirect on mobile, popup on desktop
   * @returns {Promise<Object>} User credential
   */
  const signInWithGoogle = async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();

      // Use redirect for mobile devices, popup for desktop
      if (isMobileDevice()) {
        await signInWithRedirect(auth, provider);
        // Redirect happens here - function returns immediately
        // Result will be handled by getRedirectResult in useEffect
        return null;
      } else {
        const result = await signInWithPopup(auth, provider);
        // Don't create profile here - let signup flow handle it
        return result;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Create user profile in Firestore (used after Google sign-in during signup)
   * @param {Object} user - Firebase user object
   * @param {Object} profileData - User profile data including role
   * @returns {Promise<void>}
   */
  const createUserProfile = async (user, profileData) => {
    try {
      setError(null);
      console.log('[CreateUserProfile] Creating profile for user:', user.uid);
      const userDocRef = doc(db, 'users', user.uid);

      const firestoreData = {
        uid: user.uid,
        email: user.email,
        displayName: profileData.displayName || user.displayName || '',
        photoURL: user.photoURL || '',
        role: profileData.role || 'client',
        phoneNumber: profileData.phoneNumber || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        emailVerified: user.emailVerified,
        // Role-specific data
        ...(profileData.role === 'trainer' ? {
          businessName: profileData.businessName || '',
          certifications: profileData.certifications || [],
          specializations: profileData.specializations || [],
          clientCount: 0
        } : {
          trainerId: profileData.trainerId || null,
          subscription: 'free',
          goals: profileData.goals || []
        })
      };
      console.log('[CreateUserProfile] Firestore data to be written:', firestoreData);

      await setDoc(userDocRef, firestoreData);
      console.log('[CreateUserProfile] Profile created successfully');
    } catch (error) {
      console.error('[CreateUserProfile] Error creating profile:', error);
      console.error('[CreateUserProfile] Error code:', error.code);
      console.error('[CreateUserProfile] Error message:', error.message);
      setError(error.message);
      throw error;
    }
  };

  /**
   * Sign out the current user
   * @returns {Promise<void>}
   */
  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Send password reset email
   * @param {string} email - User's email
   * @returns {Promise<void>}
   */
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  /**
   * Fetch user profile from Firestore
   * @param {string} uid - User ID
   * @returns {Promise<Object>} User profile data
   */
  const fetchUserProfile = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check ('trainer' or 'client')
   * @returns {boolean}
   */
  const hasRole = (role) => {
    return userProfile?.role === role;
  };

  /**
   * Check if user is a trainer
   * @returns {boolean}
   */
  const isTrainer = () => hasRole('trainer');

  /**
   * Check if user is a client
   * @returns {boolean}
   */
  const isClient = () => hasRole('client');

  /**
   * Reload current user's profile from Firestore
   * Useful after profile updates
   * @param {string} uid - Optional user ID (defaults to currentUser.uid)
   * @returns {Promise<void>}
   */
  const reloadUserProfile = async (uid = null) => {
    const userId = uid || currentUser?.uid;
    if (userId) {
      const profile = await fetchUserProfile(userId);
      setUserProfile(profile);
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Fetch user profile from Firestore
        const profile = await fetchUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Handle redirect result for mobile Google Sign-In
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // Successfully signed in with redirect
          console.log('Google sign-in redirect successful:', result.user.email);
          // The onAuthStateChanged listener will handle setting the user
        }
      } catch (error) {
        console.error('Redirect sign-in error:', error);
        if (error.code !== 'auth/popup-closed-by-user') {
          setError('Failed to sign in with Google. Please try again.');
        }
      }
    };

    handleRedirectResult();
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    signup,
    login,
    signInWithGoogle,
    createUserProfile,
    logout,
    resetPassword,
    hasRole,
    isTrainer,
    isClient,
    fetchUserProfile,
    reloadUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
